/**
 * HomeHeroV2 — new SocialPulse homepage hero.
 *
 * Design brief (2026-02-18): the current top area wastes ~half the
 * first screen with header rows + tagline + repeated "Browse all
 * sections" chip. This replacement answers three questions in <5s:
 *   1. What is SocialPulse? (headline + subhead)
 *   2. What can I do here? (5 pathway cards below hero)
 *   3. Where do I click first? (Place × Topic × Explore CTA)
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Tag, ArrowRight, Compass, HandHeart,
  Heart, Send, BookOpen, ChevronDown, Home as HomeIcon, Bell, Users, Clock,
} from "lucide-react";
import WorldMapHero from "@/components/home/WorldMapHero";

const PLACES = [
  { key: "",                       label: "Choose a place",         hint: "" },
  { key: "lake-forest",            label: "Lake Forest, CA",        hint: "US" },
  { key: "kerala",                 label: "Kerala",                 hint: "India" },
  { key: "kottayam",               label: "Kottayam, Kerala",       hint: "India" },
  { key: "bengaluru",              label: "Bengaluru, Karnataka",   hint: "India" },
  { key: "mumbai",                 label: "Mumbai, Maharashtra",    hint: "India" },
  { key: "portland",               label: "Portland, OR",           hint: "US" },
  { key: "worldwide",              label: "Worldwide",              hint: "Global" },
];

const TOPICS = [
  { key: "",           label: "Choose a topic",     hint: "" },
  { key: "community",  label: "Community & Events", hint: "" },
  { key: "food",       label: "Food",               hint: "" },
  { key: "travel",     label: "Travel",             hint: "" },
  { key: "health",     label: "Health",             hint: "" },
  { key: "education",  label: "Education",          hint: "" },
  { key: "family",     label: "Family & Heritage",  hint: "" },
  { key: "finance",    label: "Finance",            hint: "" },
  { key: "nature",     label: "Nature",             hint: "" },
];

const PATHWAYS = [
  {
    key: "place",  icon: Compass,
    label: "Explore a Place",
    blurb: "Discover cities, counties, states, countries and local communities.",
    color: "bg-[#1E64C8]/10 text-[#1E64C8]",
    to: "/explore",
  },
  {
    key: "topic",  icon: Tag,
    label: "Follow a Topic",
    blurb: "Food, travel, health, education, finance, family and more.",
    color: "bg-[#7B4A9C]/10 text-[#7B4A9C]",
    to: "/topics",
  },
  {
    key: "help",   icon: HandHeart,
    label: "Find or Offer Help",
    blurb: "Connect needs, volunteers, organizations and community resources.",
    color: "bg-emerald-600/10 text-emerald-700",
    to: "/find-help",
  },
  {
    key: "cause",  icon: Heart,
    label: "Support a Cause",
    blurb: "Verified local and global initiatives worth backing.",
    color: "bg-rose-600/10 text-rose-700",
    to: "/causes",
  },
  {
    key: "story",  icon: Send,
    label: "Share Your Story",
    blurb: "Publish knowledge, experiences, announcements and ideas.",
    color: "bg-[#F58220]/10 text-[#F58220]",
    to: "/publisher/write",
  },
];

// Illustration composed of remote stock (Pexels/Unsplash CDN) so the
// hero stays lightweight and doesn't require a bundled asset.
const HERO_ART = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=70";

export default function HomeHeroV2() {
  const nav = useNavigate();
  const [place, setPlace] = useState("");
  const [topic, setTopic] = useState("");

  const explore = () => {
    if (place && topic) {
      nav(`/explore?place=${place}&topic=${topic}`);
    } else if (place) {
      nav(`/explore?place=${place}`);
    } else if (topic) {
      nav(`/topics/${topic}`);
    } else {
      nav("/explore");
    }
  };

  return (
    <section
      data-testid="home-hero-v2"
      className="relative overflow-hidden"
      style={{ background: "#FDFBF7" }}
    >
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-center">
        {/* Left — copy + CTAs */}
        <div className="min-w-0">
          <div className="uppercase text-xs font-mono font-semibold tracking-[0.2em] text-[#50586C] mb-3">
            A Network for Communities
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B1226] tracking-tight leading-[1.1]"
            data-testid="home-hero-headline"
          >
            Discover Your Community.
            <br />
            Connect With the World.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#1F2333] leading-relaxed max-w-xl">
            Explore local places, helpful people, community stories, businesses,
            causes and opportunities throughout the SocialPulse network.
          </p>

          {/* Discovery pair — Place, then Topic stacked, Explore full-width below */}
          <div
            className="mt-8 max-w-md flex flex-col gap-3"
            data-testid="home-hero-discovery"
          >
            <DiscoverySelect
              icon={MapPin}
              value={place}
              onChange={setPlace}
              options={PLACES}
              testId="home-hero-place"
            />
            <DiscoverySelect
              icon={Tag}
              value={topic}
              onChange={setTopic}
              options={TOPICS}
              testId="home-hero-topic"
            />
            <button
              type="button"
              onClick={explore}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1E64C8] hover:bg-[#164FA0] text-white font-semibold rounded-xl px-6 py-3.5 text-base transition-colors shadow-sm"
              data-testid="home-hero-explore-cta"
            >
              Explore <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Secondary link row */}
          <div className="mt-5 text-sm text-[#50586C] flex items-center gap-3 flex-wrap">
            <span>Or:</span>
            <a href="/publisher/write" className="font-semibold text-[#1E64C8] hover:text-[#164FA0]">
              Share a Story
            </a>
            <span className="text-[#A29C8F]">·</span>
            <a href="/find-help" className="font-semibold text-[#1E64C8] hover:text-[#164FA0]">
              Offer Help
            </a>
            <span className="text-[#A29C8F]">·</span>
            <a href="/causes/submit" className="font-semibold text-[#1E64C8] hover:text-[#164FA0]">
              Submit a Cause
            </a>
          </div>
        </div>

        {/* Right — illustrated world map with floating photo cards */}
        <div data-testid="home-hero-art">
          <WorldMapHero />
        </div>
      </div>

      {/* Pathway cards row */}
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 pb-14">
        <ul
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
          data-testid="home-hero-pathways"
        >
          {PATHWAYS.map((p) => (
            <li key={p.key}>
              <a
                href={p.to}
                className="group block h-full rounded-2xl bg-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 p-5"
                data-testid={`home-pathway-${p.key}`}
              >
                <span className={`inline-flex items-center justify-center w-11 h-11 rounded-full ${p.color}`}>
                  <p.icon className="w-5 h-5" />
                </span>
                <h3 className="mt-3 text-base font-medium text-[#0B1226] leading-snug">
                  {p.label}
                </h3>
                <p className="mt-1 text-sm text-[#50586C] leading-relaxed">
                  {p.blurb}
                </p>
                <span className="mt-3 inline-flex items-center gap-0.5 text-xs font-semibold text-[#1E64C8] group-hover:text-[#164FA0]">
                  Get started <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ─── Building blocks ────────────────────────────────────────────

function HomeTabBar() {
  const [tab, setTab] = useState("near");
  const tabs = [
    { key: "foryou",    label: "For You",    icon: HomeIcon },
    { key: "near",      label: "Near You",   icon: MapPin },
    { key: "following", label: "Following",  icon: Users },
    { key: "latest",    label: "Latest",     icon: Clock },
    { key: "causes",    label: "Causes",     icon: Heart },
  ];
  return (
    <div className="max-w-[1360px] mx-auto px-6 lg:px-10 pb-8">
      <div
        className="mx-auto max-w-2xl bg-white rounded-full shadow-sm p-1 flex items-center gap-1"
        role="tablist"
        aria-label="Home feed filters"
        data-testid="home-tab-bar"
      >
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                active
                  ? "bg-[#1E64C8] text-white shadow-sm"
                  : "text-[#0B1226] hover:bg-[#F0F5FA]"
              }`}
              data-testid={`home-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DiscoverySelect({ icon: Icon, value, onChange, options, testId }) {
  return (
    <label className="flex-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white hover:border-slate-400 px-3 py-2.5 focus-within:ring-2 focus-within:ring-blue-300 transition">
      <Icon className="w-4 h-4 text-slate-500 shrink-0" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent text-sm text-slate-800 outline-none appearance-none pr-4"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.key || "_"} value={o.key}>
            {o.label}{o.hint ? ` — ${o.hint}` : ""}
          </option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 pointer-events-none -ml-4" />
    </label>
  );
}

function FloatingChip({ className = "", label, sub, tone = "white", big = false }) {
  const map = {
    white:  "bg-white/92 text-[#0B1226]",
    blue:   "bg-[#1E64C8]/95 text-white",
    orange: "bg-[#F58220]/95 text-white",
  };
  return (
    <div
      className={`absolute ${className} pointer-events-none rounded-xl backdrop-blur-sm shadow-lg px-3 py-2 ${map[tone]}`}
    >
      <div className={`font-bold ${big ? "text-base" : "text-xs"}`}>{label}</div>
      <div className={`opacity-85 leading-tight ${big ? "text-[11px]" : "text-[10px]"}`}>{sub}</div>
    </div>
  );
}
